import { useQuery } from 'react-query'
import { request } from 'Features/auth/axios-client'
import { WaterfallRow } from 'Common/Charts/StockWaterfall/stockWaterfall.walk'
import * as Types from './StockProjectionException.types'

const fetchStockProjectExceptionsData = async ({
  planId,
  sourcesiteId,
  workcentreId,
  weekRangeConfig,
}: Types.StockProjectionTableParams) => {
  const { data } = await request({
    method: 'post',
    url: `plan/${planId}/source-sites/${sourcesiteId}/workcentres/${workcentreId}/stock-projections-exceptions-table`,
    data: weekRangeConfig,
  })
  return data
}
export const useStockProjectExceptionsData = (
  queryParams: Types.StockProjectionTableParams
) => {
  return useQuery(
    [
      'stock-projection-exception-table',
      queryParams.planId,
      queryParams.sourcesiteId,
      queryParams.workcentreId,
      queryParams.weekRangeConfig,
    ],
    () => fetchStockProjectExceptionsData(queryParams),
    {
      select: (data) => data?.data,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  )
}

const fetchStockProjectExceptionsGraphData = async ({
  planId,
  sourcesiteId,
  workcentreId,
  skuCodes,
}: Types.StockProjectionExceptionGraphURLParams) => {
  const { data } = await request({
    method: 'post',
    url: `plan/${planId}/source-sites/${sourcesiteId}/workcentres/${workcentreId}/stock-projections-exceptions-graph`,
    data: {
      sku_codes: skuCodes,
    },
  })
  return data
}
export const useStockProjectExceptionsGraphData = (
  queryParams: Types.StockProjectionExceptionGraphURLParams
) => {
  return useQuery(
    [
      'stock-projection-exception-graph',
      queryParams.planId,
      queryParams.sourcesiteId,
      queryParams.workcentreId,
      queryParams.skuCodes,
    ],
    () => fetchStockProjectExceptionsGraphData(queryParams),
    {
      select: (data) => data?.data,
      enabled: queryParams.skuCodes?.length >= 0 ? true : false,
      refetchOnWindowFocus: false,
      cacheTime: 0,
    }
  )
}

// Stock waterfall v2 (R1). Constrained always: the arm Classic shows.
const fetchStockProjectionWaterfall = async ({
  planId,
  sourcesiteId,
  workcentreId,
  skuCodes,
}: Types.StockProjectionWaterfallParams) => {
  const { data } = await request({
    method: 'post',
    url: `plan/${planId}/source-sites/${sourcesiteId}/workcentres/${workcentreId}/stock-projections-exceptions-waterfall`,
    data: {
      sku_codes: skuCodes,
      planned_version: 'Constrained',
    },
  })
  return data
}

// Succeeds on a v2 plan (the work centre has a ledger partition); fails with
// LEDGER_NOT_AVAILABLE on a v1 plan. An empty selection reads no ledger rows.
export const useStockProjectionWaterfallAvailability = (
  queryParams: Types.StockProjectionWaterfallParams
) => {
  return useQuery(
    [
      'stock-projection-waterfall-availability',
      queryParams.planId,
      queryParams.sourcesiteId,
      queryParams.workcentreId,
    ],
    () => fetchStockProjectionWaterfall({ ...queryParams, skuCodes: [] }),
    {
      enabled: queryParams.enabled,
      refetchOnWindowFocus: false,
    }
  )
}

export const useStockProjectionWaterfallData = (
  queryParams: Types.StockProjectionWaterfallParams
) => {
  return useQuery(
    [
      'stock-projection-waterfall',
      queryParams.planId,
      queryParams.sourcesiteId,
      queryParams.workcentreId,
      queryParams.skuCodes,
    ],
    () => fetchStockProjectionWaterfall(queryParams),
    {
      select: (data): WaterfallRow[] => data?.data ?? [],
      enabled: queryParams.enabled,
      refetchOnWindowFocus: false,
    }
  )
}
